import type { Reference, Resource } from '@medplum/fhirtypes'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { extractId } from '@seen/core/utils'
import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

type AnyResourceClass = ExtendedResourceConstructor<ExtendedResource>

type ResourceInstance<TClass> =
  TClass extends ExtendedResourceConstructor<infer T> ? T : never

// Matches the server's default `_history` page, so the first page a caller sees
// is the same one the unpaged read used to return. Resources that accumulate
// thousands of versions (collaborative desks write a version per edit) are only
// reachable past that first page by walking `_offset`, so callers that need
// older versions page back explicitly rather than loading the whole history.
export const RESOURCE_VERSIONS_PAGE_SIZE = 100

export interface ResourceVersionPage<T extends ExtendedResource> {
  versions: T[]
  // Raw bundle entry count, not `versions.length`: parsing drops entries that
  // fail `isInstance` or `fromFhirResource`, so a full page can yield zero
  // versions. Advancing the cursor on the parsed count would stop paging early.
  entryCount: number
}

type ResourceVersionsResult<T extends ExtendedResource> =
  UseInfiniteQueryResult<T[], Error> & { isHistoryComplete: boolean }

export interface UseResourceVersionsOptions<T extends ExtendedResource> {
  query?: Omit<
    UseInfiniteQueryOptions<
      ResourceVersionPage<T>,
      Error,
      T[],
      ReturnType<typeof cacheKeys.readHistoryExtended>,
      number
    >,
    | 'getNextPageParam'
    | 'initialData'
    | 'initialPageParam'
    | 'queryFn'
    | 'queryKey'
    | 'select'
  >
  validate?: (version: T) => void
}

export interface UseBulkResourceVersionsOptions<T extends ExtendedResource> {
  query?: Omit<
    UseQueryOptions<
      T[],
      Error,
      T[],
      ReturnType<typeof cacheKeys.bulkReadHistoryExtended>
    >,
    'initialData' | 'queryFn' | 'queryKey'
  >
  validate?: (version: T) => void
}

/*
 * Module-level so React Query can memoize the flattened result — an inline
 * arrow would re-run `select` and hand callers a new array every render.
 *
 * Dedupes because `_offset` is a position, not a cursor, and `_history` is
 * newest-first: a version written between two page reads shifts every entry down
 * one, so the next fixed offset re-serves versions the previous page already
 * returned. That is the normal case for a desk being actively edited. Keeping
 * the first occurrence preserves newest-first order and keeps `versionId` unique,
 * which the version list relies on for its React keys.
 */
function flattenVersionPages<T extends ExtendedResource>(
  data: InfiniteData<ResourceVersionPage<T>, number>
): T[] {
  const seen = new Set<string>()
  return data.pages
    .flatMap((page) => page.versions)
    .filter((version) => {
      if (seen.has(version.versionId)) {
        return false
      }
      seen.add(version.versionId)
      return true
    })
}

export function useResourceVersions<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  id?:
    | Reference<NoInfer<ResourceOf<T>>>
    | string
    | NoInfer<ResourceOf<T>>
    | null,
  options?: UseResourceVersionsOptions<T>
): ResourceVersionsResult<T>

export function useResourceVersions<
  const TClasses extends readonly AnyResourceClass[],
>(
  resourceClasses: TClasses,
  id?: Reference | Resource | string | null,
  options?: UseResourceVersionsOptions<ResourceInstance<TClasses[number]>>
): ResourceVersionsResult<ResourceInstance<TClasses[number]>>

export function useResourceVersions<T extends ExtendedResource>(
  resourceClasses:
    | ExtendedResourceConstructor<T>
    | readonly ExtendedResourceConstructor<T>[],
  id?: Reference | Resource | string | null,
  options: UseResourceVersionsOptions<T> = {}
): ResourceVersionsResult<T> {
  const medplum = useMedplum()
  const pagingEnabled = useFeatureFlag(featureFlags.versionHistoryPaging)
  const queryClient = useQueryClient()
  const resolvedId = extractId(id) ?? ''
  const classes = Array.isArray(resourceClasses)
    ? resourceClasses
    : [resourceClasses]
  const resourceType = classes.at(0)?.RESOURCE_TYPE
  invariant(
    classes.every(
      (resourceClass) => resourceClass.RESOURCE_TYPE === resourceType
    ),
    'Resource classes must all have the same resource type'
  )
  const className = classes.map((resourceClass) => resourceClass.name).join('|')
  const enabled =
    Boolean(resourceType) &&
    Boolean(resolvedId) &&
    classes.length > 0 &&
    (options.query?.enabled === undefined || options.query.enabled)

  const queryResult = useInfiniteQuery<
    ResourceVersionPage<T>,
    Error,
    T[],
    ReturnType<typeof cacheKeys.readHistoryExtended>,
    number
  >({
    ...options.query,
    enabled,
    // Keep the readHistory marker in this key so historical versions are not
    // cached over the latest resource by QueryProvider. Pages live under this
    // one key as `InfiniteData`, so paging back never creates a key that escapes
    // that guard.
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.readHistoryExtended(
      resourceType,
      resolvedId,
      className
    ),
    queryFn: async ({ pageParam }) => {
      invariant(resourceType, 'Resource type is required')
      invariant(resolvedId, 'Resource ID is required')

      // Flag off reads history exactly as it always has — no `_count`, no
      // `_offset` — so the off state is the shipped request, not a re-derivation
      // of it.
      const history = await medplum.readHistory(
        resourceType,
        resolvedId,
        pagingEnabled
          ? { count: RESOURCE_VERSIONS_PAGE_SIZE, offset: pageParam }
          : undefined
      )
      const entries = history.entry ?? []
      return {
        versions: parseResourceVersions(
          classes,
          entries.map((entry) => entry.resource),
          resolvedId,
          options.validate
        ),
        entryCount: entries.length,
      }
    },
    initialPageParam: 0,
    // A short page means the server has nothing left to hand back, which is the
    // only end-of-history signal `_history` gives us. With the flag off there is
    // never a next page, so the query settles on the single unpaged read.
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      !pagingEnabled || lastPage.entryCount < RESOURCE_VERSIONS_PAGE_SIZE
        ? undefined
        : lastPageParam + RESOURCE_VERSIONS_PAGE_SIZE,
    select: flattenVersionPages,
    meta: {
      ...options.query?.meta,
      queryParams: {
        _id: resolvedId,
      },
      resourceClass: classes.at(0),
    },
  })

  useErrorToast(
    queryResult.error &&
      enabled &&
      `Error accessing history for ${resourceType}/${resolvedId}: ${queryResult.error.message ?? 'Unknown error'}`
  )

  // Mirrors the `useQuery` wrapper: a disabled query still serves cached data,
  // which callers that opted out do not expect to see.
  if (!enabled) {
    return {
      ...queryResult,
      data: undefined,
      isHistoryComplete: false,
    } as ResourceVersionsResult<T>
  }

  const pages = queryClient.getQueryData<
    InfiniteData<ResourceVersionPage<T>, number>
  >(cacheKeys.readHistoryExtended(resourceType, resolvedId, className))?.pages

  return {
    ...queryResult,
    isHistoryComplete:
      !!pages?.length &&
      (pages.at(-1)?.entryCount ?? RESOURCE_VERSIONS_PAGE_SIZE) <
        RESOURCE_VERSIONS_PAGE_SIZE &&
      pages.every((page) => page.entryCount === page.versions.length),
  }
}

export function useBulkResourceVersions<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  ids?:
    | readonly (
        | Reference<NoInfer<ResourceOf<T>>>
        | string
        | NoInfer<ResourceOf<T>>
        | null
        | undefined
      )[]
    | null,
  options?: UseBulkResourceVersionsOptions<T>
): UseQueryResult<T[]>

export function useBulkResourceVersions<
  const TClasses extends readonly AnyResourceClass[],
>(
  resourceClasses: TClasses,
  ids?: readonly (Reference | Resource | string | null | undefined)[] | null,
  options?: UseBulkResourceVersionsOptions<ResourceInstance<TClasses[number]>>
): UseQueryResult<ResourceInstance<TClasses[number]>[]>

export function useBulkResourceVersions<T extends ExtendedResource>(
  resourceClasses:
    | ExtendedResourceConstructor<T>
    | readonly ExtendedResourceConstructor<T>[],
  ids?: readonly (Reference | Resource | string | null | undefined)[] | null,
  options: UseBulkResourceVersionsOptions<T> = {}
): UseQueryResult<T[]> {
  const medplum = useMedplum()
  const classes = Array.isArray(resourceClasses)
    ? resourceClasses
    : [resourceClasses]
  const resourceType = classes.at(0)?.RESOURCE_TYPE
  invariant(
    classes.every(
      (resourceClass) => resourceClass.RESOURCE_TYPE === resourceType
    ),
    'Resource classes must all have the same resource type'
  )
  const className = classes.map((resourceClass) => resourceClass.name).join('|')
  const resolvedIds =
    ids?.map((id) => extractId(id)).filter((id): id is string => !!id) ?? []
  const enabled =
    Boolean(resourceType) &&
    resolvedIds.length > 0 &&
    classes.length > 0 &&
    (options.query?.enabled === undefined || options.query.enabled)

  const queryResult = useQuery<
    T[],
    Error,
    T[],
    ReturnType<typeof cacheKeys.bulkReadHistoryExtended>
  >({
    ...options.query,
    enabled,
    // Keep the readHistory marker in this key so historical versions are not
    // cached over the latest resource by QueryProvider.
    queryKey: cacheKeys.bulkReadHistoryExtended(
      resourceType,
      resolvedIds,
      className
    ),
    queryFn: async () => {
      invariant(resourceType, 'Resource type is required')

      const histories = await Promise.all(
        resolvedIds.map(async (resolvedId) => {
          try {
            const history = await medplum.readHistory(resourceType, resolvedId)
            return parseResourceVersions(
              classes,
              history.entry?.map((entry) => entry.resource),
              resolvedId,
              options.validate
            )
          } catch (error) {
            console.error(
              `Error reading ${resourceType}/${resolvedId}/_history:`,
              error
            )
            return []
          }
        })
      )

      return histories.flat()
    },
    meta: {
      ...options.query?.meta,
      queryParams: {
        _id: resolvedIds,
      },
      resourceClass: classes.at(0),
    },
  })

  return queryResult
}

export function parseResourceVersions<T extends ExtendedResource>(
  resourceClasses: readonly ExtendedResourceConstructor<T>[],
  resources: (Resource | undefined)[] | undefined,
  fallbackId: string,
  validate?: (version: T) => void
): T[] {
  const resourceType = resourceClasses.at(0)?.RESOURCE_TYPE
  invariant(
    resourceClasses.every(
      (resourceClass) => resourceClass.RESOURCE_TYPE === resourceType
    ),
    'Resource classes must all have the same resource type'
  )
  return (
    resources
      ?.map((resource, index) =>
        parseResourceVersion(resourceClasses, resource, {
          fallbackId,
          index,
          validate,
        })
      )
      .filter((version): version is T => !!version) ?? []
  )
}

function parseResourceVersion<T extends ExtendedResource>(
  resourceClasses: readonly ExtendedResourceConstructor<T>[],
  resource: Resource | undefined,
  {
    fallbackId,
    index,
    validate,
  }: {
    fallbackId: string
    index: number
    validate?: (version: T) => void
  }
): T | undefined {
  if (!resource) {
    return undefined
  }

  const resourceClass = resourceClasses.find((candidate) =>
    candidate.isInstance(resource)
  )

  if (!resourceClass) {
    return undefined
  }

  try {
    const version = resourceClass.fromFhirResource(resource as ResourceOf<T>)
    validate?.(version)
    return version
  } catch (error) {
    console.error(
      `Error parsing ${resourceClass.name} history version ${resource.resourceType}/${resource.id ?? fallbackId}${resource.meta?.versionId ? `/_history/${resource.meta.versionId}` : ` at index ${index}`}:`,
      error
    )
    return undefined
  }
}
